import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import Image from "../components/Image"
import {
  Award,
  Users,
  Globe,
  Target,
  Heart,
  Lightbulb,
  Shield,
  TrendingUp,
  Calendar,
  MapPin,
  Mail,
  Linkedin,
} from "lucide-react"

const AboutPage: React.FC = () => {
  const timeline = [
    {
      year: "2003",
      title: "Company Founded",
      description: "MPV established with a vision to revolutionize multipurpose vehicle solutions in Australia",
      milestone: "Founded",
    },
    {
      year: "2005",
      title: "First Multihog Delivery",
      description:
        "Delivered our first Multihog tractor to Melbourne City Council, beginning our municipal partnership journey",
      milestone: "First Delivery",
    },
    {
      year: "2008",
      title: "Airport Operations Expansion",
      description: "Expanded into airport operations with specialized runway maintenance solutions",
      milestone: "Airport Entry",
    },
    {
      year: "2012",
      title: "Mining Sector Growth",
      description: "Entered the mining sector with heavy-duty applications and specialized attachments",
      milestone: "Mining Expansion",
    },
    {
      year: "2015",
      title: "100th Unit Milestone",
      description: "Celebrated delivery of our 100th Multihog unit, establishing market leadership",
      milestone: "100 Units",
    },
    {
      year: "2018",
      title: "Innovation Center",
      description: "Opened our state-of-the-art innovation center for attachment development and testing",
      milestone: "Innovation Hub",
    },
    {
      year: "2020",
      title: "Digital Transformation",
      description: "Launched digital service platform and remote diagnostics capabilities",
      milestone: "Digital Era",
    },
    {
      year: "2023",
      title: "Sustainability Initiative",
      description: "Introduced eco-friendly attachments and sustainable operation solutions",
      milestone: "Green Future",
    },
  ]

  const teamMembers = [
    {
      name: "Michael Thompson",
      position: "Chief Executive Officer",
      bio: "With over 25 years in the heavy machinery industry, Michael founded MPV with a vision to transform multipurpose vehicle solutions. His leadership has driven the company's growth from a startup to Australia's premier Multihog specialist.",
      image: "https://via.placeholder.com/300x300?text=Michael+Thompson",
      linkedin: "#",
      email: "michael.thompson@mpv-multihog.com.au",
    },
    {
      name: "Sarah Chen",
      position: "Chief Technology Officer",
      bio: "Sarah leads our innovation and product development initiatives. With a background in mechanical engineering and 15 years at MPV, she's responsible for developing cutting-edge attachments and improving Multihog performance.",
      image: "https://via.placeholder.com/300x300?text=Sarah+Chen",
      linkedin: "#",
      email: "sarah.chen@mpv-multihog.com.au",
    },
    {
      name: "David Rodriguez",
      position: "Head of Sales & Partnerships",
      bio: "David manages our national sales team and partnership network. His deep understanding of customer needs and market dynamics has been instrumental in expanding MPV's reach across Australia.",
      image: "https://via.placeholder.com/300x300?text=David+Rodriguez",
      linkedin: "#",
      email: "david.rodriguez@mpv-multihog.com.au",
    },
    {
      name: "Emma Wilson",
      position: "Operations Director",
      bio: "Emma oversees all operational aspects of MPV, from supply chain management to service delivery. Her operational excellence ensures our customers receive the highest quality products and support.",
      image: "https://via.placeholder.com/300x300?text=Emma+Wilson",
      linkedin: "#",
      email: "emma.wilson@mpv-multihog.com.au",
    },
    {
      name: "James Mitchell",
      position: "Head of Engineering",
      bio: "James leads our engineering team in developing innovative solutions for complex customer challenges. His expertise in hydraulic systems and attachment design has resulted in numerous industry-first innovations.",
      image: "https://via.placeholder.com/300x300?text=James+Mitchell",
      linkedin: "#",
      email: "james.mitchell@mpv-multihog.com.au",
    },
    {
      name: "Lisa Park",
      position: "Customer Success Manager",
      bio: "Lisa ensures our customers achieve maximum value from their Multihog investments. Her dedication to customer satisfaction and comprehensive support programs has earned MPV industry-leading retention rates.",
      image: "https://via.placeholder.com/300x300?text=Lisa+Park",
      linkedin: "#",
      email: "lisa.park@mpv-multihog.com.au",
    },
  ]

  const values = [
    {
      icon: Lightbulb,
      title: "Innovation",
      description:
        "We continuously push the boundaries of what's possible in multipurpose vehicle technology, developing solutions that set new industry standards.",
    },
    {
      icon: Shield,
      title: "Reliability",
      description:
        "Our customers depend on us for critical operations. We deliver products and services that perform consistently in the most demanding conditions.",
    },
    {
      icon: Heart,
      title: "Customer Focus",
      description:
        "Every decision we make is guided by our commitment to delivering exceptional value and support to our customers.",
    },
    {
      icon: Users,
      title: "Partnership",
      description:
        "We build long-term relationships with our customers, partners, and team members based on trust, transparency, and mutual success.",
    },
    {
      icon: Target,
      title: "Excellence",
      description:
        "We strive for excellence in everything we do, from product quality to customer service, setting the highest standards in our industry.",
    },
    {
      icon: Globe,
      title: "Sustainability",
      description:
        "We're committed to developing environmentally responsible solutions that help our customers achieve their sustainability goals.",
    },
  ]

  const achievements = [
    {
      number: "500+",
      label: "Units Deployed",
      description: "Multihog tractors operating across Australia",
    },
    {
      number: "40+",
      label: "Specialized Attachments",
      description: "Innovative solutions for every application",
    },
    {
      number: "150+",
      label: "Municipal Partners",
      description: "Cities and towns trusting MPV solutions",
    },
    {
      number: "50+",
      label: "Airport Clients",
      description: "Airports relying on our runway solutions",
    },
    {
      number: "95%",
      label: "Customer Satisfaction",
      description: "Industry-leading satisfaction ratings",
    },
    {
      number: "20+",
      label: "Years Experience",
      description: "Decades of innovation and expertise",
    },
  ]

  const certifications = [
    {
      name: "ISO 9001:2015",
      description: "Quality Management Systems",
      year: "2018",
    },
    {
      name: "ISO 14001:2015",
      description: "Environmental Management",
      year: "2020",
    },
    {
      name: "CASA Approved",
      description: "Aviation Equipment Certification",
      year: "2019",
    },
    {
      name: "Australian Made",
      description: "Certified Australian Manufacturing",
      year: "2003",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              About MPV
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-red-500">20+ Years</span> of
              <br />
              Innovation & Excellence
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              From our humble beginnings to becoming Australia's premier multipurpose vehicle specialists, our journey
              has been driven by innovation, customer focus, and an unwavering commitment to excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Get in Touch
              </Link>
              <Link
                to="/partners"
                className="border border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Join Our Network
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Our Mission & Vision</h2>

                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-3">Mission</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    To provide innovative, reliable, and versatile multipurpose vehicle solutions that empower our
                    customers to achieve operational excellence while contributing to sustainable and efficient
                    infrastructure management.
                  </p>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-red-600 mb-3">Vision</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    To be the global leader in multipurpose vehicle technology, setting new standards for innovation,
                    sustainability, and customer success in every market we serve.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-red-600 mb-3">Purpose</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    We exist to transform how organizations approach infrastructure maintenance and management,
                    providing solutions that are not just efficient and effective, but also environmentally responsible
                    and economically sustainable.
                  </p>
                </div>
              </div>

              <div className="relative">
                <Image
                  src="https://via.placeholder.com/600x500?text=MPV+Team+at+Work"
                  alt="MPV Team"
                  className="w-full h-auto rounded-xl shadow-lg"
                  width={600}
                  height={500}
                />
                <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-lg shadow-lg">
                  <div className="text-3xl font-bold">20+</div>
                  <div className="text-sm">Years of Excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Journey</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Two decades of innovation, growth, and customer success that have shaped MPV into the industry leader we
                are today.
              </p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-red-200"></div>

              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? "flex-row" : "flex-row-reverse"}`}>
                    <div className={`w-1/2 ${index % 2 === 0 ? "pr-8 text-right" : "pl-8 text-left"}`}>
                      <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="text-red-600 font-bold text-lg mb-2">{item.year}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>

                    {/* Timeline dot */}
                    <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-red-600 rounded-full border-4 border-white shadow-lg">
                      <Calendar className="h-6 w-6 text-white" />
                    </div>

                    <div className="w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The principles that guide everything we do and shape our culture of excellence and innovation.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                  <div className="bg-red-100 rounded-full p-4 w-16 h-16 mb-6 flex items-center justify-center">
                    <value.icon className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Meet Our Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                The passionate professionals behind MPV's success, bringing decades of combined experience and expertise
                to serve our customers.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <Image
                    src={member.image || "/placeholder.svg"}
                    alt={member.name}
                    className="w-full h-64 object-cover"
                    width={300}
                    height={300}
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-red-600 font-semibold mb-4">{member.position}</p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{member.bio}</p>
                    <div className="flex space-x-3">
                      <a
                        href={member.linkedin}
                        className="p-2 bg-gray-100 hover:bg-red-100 rounded-lg transition-colors"
                        aria-label={`${member.name} LinkedIn`}
                      >
                        <Linkedin className="h-4 w-4 text-gray-600 hover:text-red-600" />
                      </a>
                      <a
                        href={`mailto:${member.email}`}
                        className="p-2 bg-gray-100 hover:bg-red-100 rounded-lg transition-colors"
                        aria-label={`Email ${member.name}`}
                      >
                        <Mail className="h-4 w-4 text-gray-600 hover:text-red-600" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Achievements</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Numbers that reflect our commitment to excellence and the trust our customers place in us.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {achievements.map((achievement, index) => (
                <div key={index} className="text-center">
                  <div className="bg-red-600 text-white rounded-lg p-6 mb-4">
                    <div className="text-2xl md:text-3xl font-bold mb-2">{achievement.number}</div>
                    <div className="text-sm font-medium">{achievement.label}</div>
                  </div>
                  <p className="text-gray-600 text-sm">{achievement.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Certifications & Standards</h2>
              <p className="text-lg text-gray-600">
                Our commitment to quality and excellence is recognized through industry certifications
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {certifications.map((cert, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center">
                  <Award className="h-12 w-12 text-red-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{cert.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{cert.description}</p>
                  <p className="text-red-600 font-semibold text-sm">Since {cert.year}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Culture Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Culture</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  At MPV, we believe that our people are our greatest asset. We've built a culture that encourages
                  innovation, collaboration, and continuous learning, where every team member is empowered to contribute
                  to our shared success.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <TrendingUp className="h-6 w-6 text-red-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Continuous Learning</h3>
                      <p className="text-gray-600">
                        We invest in our team's professional development and encourage continuous skill enhancement.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Users className="h-6 w-6 text-red-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Collaborative Environment</h3>
                      <p className="text-gray-600">
                        We foster open communication and teamwork across all departments and levels.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <Lightbulb className="h-6 w-6 text-red-600 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Innovation Focus</h3>
                      <p className="text-gray-600">
                        We encourage creative thinking and provide resources for innovative solutions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Image
                  src="https://via.placeholder.com/300x200?text=Team+Meeting"
                  alt="Team Meeting"
                  className="w-full h-48 object-cover rounded-lg"
                  width={300}
                  height={200}
                />
                <Image
                  src="https://via.placeholder.com/300x200?text=Innovation+Lab"
                  alt="Innovation Lab"
                  className="w-full h-48 object-cover rounded-lg"
                  width={300}
                  height={200}
                />
                <Image
                  src="https://via.placeholder.com/300x200?text=Training+Session"
                  alt="Training Session"
                  className="w-full h-48 object-cover rounded-lg"
                  width={300}
                  height={200}
                />
                <Image
                  src="https://via.placeholder.com/300x200?text=Team+Building"
                  alt="Team Building"
                  className="w-full h-48 object-cover rounded-lg"
                  width={300}
                  height={200}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Location</h2>
              <p className="text-lg text-gray-600">
                Strategically located in Melbourne to serve customers across Australia
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">MPV Headquarters</h3>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <MapPin className="h-6 w-6 text-red-600 mr-3 mt-1" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                        <p className="text-gray-600">
                          123 Industrial Drive
                          <br />
                          Melbourne, VIC 3000
                          <br />
                          Australia
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Users className="h-6 w-6 text-red-600 mr-3 mt-1" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Facilities</h4>
                        <p className="text-gray-600">
                          50,000 sq ft facility including manufacturing, R&D, training center, and parts warehouse
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Globe className="h-6 w-6 text-red-600 mr-3 mt-1" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Service Coverage</h4>
                        <p className="text-gray-600">
                          Nationwide service network with authorized dealers and service centers across Australia
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <Link
                      to="/contact"
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
                    >
                      Visit Our Facility
                    </Link>
                  </div>
                </div>
              </div>

              <div className="relative">
                <Image
                  src="https://via.placeholder.com/600x400?text=MPV+Facility"
                  alt="MPV Facility"
                  className="w-full h-auto rounded-xl shadow-lg"
                  width={600}
                  height={400}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h4 className="text-xl font-bold mb-2">State-of-the-Art Facility</h4>
                  <p className="text-sm">Manufacturing, R&D, and training all under one roof</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Join the MPV Family?</h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Whether you're looking for innovative solutions, partnership opportunities, or want to join our team, we'd
              love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Get in Touch
              </Link>
              <Link
                to="/partners"
                className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors inline-block"
              >
                Explore Partnerships
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AboutPage
